import React from "react";
import { timeInputWrapperStyle, getTimeInputErrorStyle } from "../utils/constants";
import { useTimeInputHandlers } from "../hooks/useTimeInputHandlers";
import TimePickerDropdown from "./TimePickerDropdown";

/**
 * TimeInputWithPicker Component
 * Combines time input field with time picker dropdown
 * Handles all event handlers internally using useTimeInputHandlers hook
 */
const TimeInputWithPicker = ({
  pickerType, // 'start24', 'end24', 'start12', 'end12'
  value,
  setValue,
  inputRef,
  timePickerRefs, // Dropdown container refs (for scrolling)
  allInputRefs, // All input field refs (for focus checking) - optional
  timePickerOpen,
  setTimePickerOpen,
  timePickerSelection,
  setTimePickerSelection,
  timePickerFilter,
  setTimePickerFilter,
  isSettingFromDropdownRef,
  justOpenedDropdownRef,
  setSplitted,
  setSplittedDate,
  setIsTimeInputActive,
  manualChangeStartTime,
  manualChangeStartTime24,
  manualChangeEndTime,
  manualChangeEndTime24,
  error = false,
  splitted = false,
  is24Hour = true,
  className = "edit-time edit-time-manual",
  name = "time",
}) => {
  // Determine which manual change handler to use based on picker type
  const getManualChangeHandler = () => {
    switch (pickerType) {
      case "start24":
        return manualChangeStartTime24;
      case "end24":
        return manualChangeEndTime24;
      case "start12":
        return manualChangeStartTime;
      case "end12":
        return manualChangeEndTime;
      default:
        return null;
    }
  };

  const manualChangeHandler = getManualChangeHandler();

  // Use the custom hook for all event handlers
  // timePickerRefs contains the dropdown container refs (needed for scrolling)
  // allInputRefs contains all input field refs (needed for focus checking)
  const handlers = useTimeInputHandlers({
    pickerType,
    value,
    setValue,
    inputRef,
    timePickerRefs, // Pass dropdown refs directly for scrolling
    allInputRefs, // Pass all input refs for focus checking
    timePickerOpen,
    setTimePickerOpen,
    setTimePickerSelection,
    isSettingFromDropdownRef,
    justOpenedDropdownRef,
    setSplitted,
    setSplittedDate,
    setIsTimeInputActive,
    manualChangeHandler,
    is24Hour,
  });

  // Determine error style based on format and splitted state
  const errorStyle = is24Hour
    ? getTimeInputErrorStyle(error)
    : getTimeInputErrorStyle(error, splitted);

  // Determine input name based on picker type
  const inputName = pickerType.includes("start") ? "start_time" : "end_time";

  return (
    <div style={timeInputWrapperStyle}>
      <input
        ref={inputRef}
        style={errorStyle}
        type="text"
        name={inputName}
        autoComplete="off"
        value={value}
        onChange={handlers.onChange}
        onClick={handlers.onClick}
        onBlur={handlers.onBlur}
        onFocus={handlers.onFocus}
        className={`${className} ${pickerType.includes("start") ? "edit-time-start" : "edit-time-end"}`}
      />
      <TimePickerDropdown
        pickerType={pickerType}
        inputValue={value}
        is24Hour={is24Hour}
        inputRef={inputRef}
        position="bottom"
        isOpen={timePickerOpen[pickerType]}
        timePickerSelection={timePickerSelection}
        setTimePickerSelection={setTimePickerSelection}
        timePickerRefs={timePickerRefs}
        isSettingFromDropdownRef={isSettingFromDropdownRef}
        setTimePickerOpen={setTimePickerOpen}
        setTimePickerFilter={setTimePickerFilter}
        setIsTimeInputActive={setIsTimeInputActive}
        manualChangeStartTime={manualChangeStartTime}
        manualChangeStartTime24={manualChangeStartTime24}
        manualChangeEndTime={manualChangeEndTime}
        manualChangeEndTime24={manualChangeEndTime24}
      />
    </div>
  );
};

export default TimeInputWithPicker;
